import React, {
	useState,
	useLayoutEffect,
	useEffect,
	useCallback,
	useMemo,
	useRef,
	Dispatch,
} from "react";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { Button } from "uikit";

import { SIP, SIPToDispatcher } from "../../../../../../../../services";
import sipToDispatcher from "../../../../../../../../redux/reducers/sipToDispatcher";
import { useTypedSelector } from "../../../../../../../../redux/store";
import { sipDisconnect } from "../../../../../../../../utils/jsSip";
import sipFlowLog from "../../../../../../../../utils/sipFlowLog";
import { Divider } from "../../../../../../../common";
import { MultiSelectWithModalBase } from "../../../../../../../common/Modal";
import LabeledField from "../../../../../../../LabeledField";
import Loader from "../../../../../../../Loader";
import DeleteModal from "../../../../../../../DeleteModal";

import Sip from "./components/Sip";

const ControlButton = styled(Button.Button)`
	height: 32px;
	min-width: 32px;
	width: 200px;
	border: 1px solid;
	border-color: #dee0e2;
	border-radius: 5px;
`;

const StyledLabeledField = styled(LabeledField)`
	// margin: 0 0 0.75rem 0;
	width: 200px;
	color: rgba(0, 0, 0, 0.87);
	justify-items: center;
	text-align: center;
	font-weight: 700;
	font-size: 1.1rem;
	line-height: 1.3;
`;

const Message = styled.div`
	padding-top: 10px;
	cursor: default;
`;

const DividerCustomer = styled(Divider)`
	margin-top: 10px;
	margin-bottom: 10px;
`;

const PopupContent: React.FC<PopupContent.Props> = ({
	onClose,
	setShowModalExternal,
	userId,
	setSelectOptionsExternal,
	choiseSIPidExternal,
	setChoiseSIPidExternal,
}) => {
	const { t } = useTranslation();
	const dispatch = useDispatch();

	const callsData = useTypedSelector(
		(state) => state.softphoneWidget.callsData,
	);

	const textTranslate = useMemo(
		() => ({
			internalSIP: t("main_header.sip.internal") ?? "",
			externalSIP: t("main_header.sip.external") ?? "",
			emptySIP: t("main_header.sip.none2") ?? "",
			lines: t("main_header.sip.lines") ?? "",
			internalLine: t("main_header.sip.internal_line") ?? "",
			externalLine: t("main_header.sip.external_line") ?? "",
			sipAlert: t("main_header.sip.alert") ?? "",
			noAvailable: t("main_header.sip.no_available") ?? "",
		}),
		[t],
	);

	const [isLoading, setIsLoading] = useState(true);
	const [isHardPhoneSipsListEmpty, setIsHardPhoneSipsListEmpty] =
		useState(false);
	const [isSoftPhoneSipsListEmpty, setIsSoftPhoneSipsListEmpty] =
		useState(false);
	const [softPhoneSipsData, setSoftPhoneSipsData] = useState<
		SIP.Model.Available[]
	>([]);
	const [hardPhoneSipsData, setHardPhoneSipsData] = useState<
		SIP.Model.Available[]
	>([]);

	const getSips = useCallback(async () => {
		try {
			const sips =
				(await SIPToDispatcher.getAvailable({
					dispatcherId: userId,
					status: "ALL",
				})) || [];

			sipFlowLog("Popup: getAvailable result", {
				dispatcherId: userId,
				lines: sips.map((sip) => ({
					sipValue: sip.sipValue,
					claimSipId: sip.sipToDispatcherId,
					linkId: sip.sipId,
					isSoftPhone: sip.isSoftPhone,
					occupiedByUserId: sip.occupiedByUserId,
					lastCheckMs: sip.lastCheckMs,
				})),
			});

			const filtredHardPhoneSips = sips.filter((sip) => !sip.isSoftPhone);
			setIsHardPhoneSipsListEmpty(!filtredHardPhoneSips.length);

			const filtresSoftPhoneSips = sips.filter((sip) => sip.isSoftPhone);
			setIsSoftPhoneSipsListEmpty(!filtresSoftPhoneSips.length);

			setHardPhoneSipsData(filtredHardPhoneSips);
			setSoftPhoneSipsData(filtresSoftPhoneSips);

			return {
				softPhoneSips: filtresSoftPhoneSips,
				hardPhoneSips: filtredHardPhoneSips,
			};
		} catch (error) {
			console.error("[SIP] getAvailable failed", error);
			setIsHardPhoneSipsListEmpty(true);
			setIsSoftPhoneSipsListEmpty(true);
			setHardPhoneSipsData([]);
			setSoftPhoneSipsData([]);
			return { softPhoneSips: [], hardPhoneSips: [] };
		} finally {
			setIsLoading(false);
		}
	}, [userId]);

	const onSubmitSip = useCallback(
		// `sipId` is the SIP id (getAvailable.sipToDispatcherId): the Redis
		// claim/session is keyed by it. Everything else (getById, call filter,
		// callToPhone) resolves by the link id (sipToDispatcher.id), returned by
		// getAvailable as `sipId`. Keep the two apart to avoid wrong-line lookups.
		async (sipId: number) => {
			sipFlowLog("Popup: claim start", { claimSipId: sipId });
			const data = await SIPToDispatcher.initSession({
				sipToDispatcherId: sipId,
			});
			sipFlowLog("Popup: claim result", {
				claimSipId: sipId,
				success: data?.success === true,
			});

			if (data.success) {
				const selectedHardPhoneSip = hardPhoneSipsData.find(
					(sip) => sip.sipToDispatcherId === sipId,
				);
				const selectedSoftPhoneSip = softPhoneSipsData.find(
					(sip) => sip.sipToDispatcherId === sipId,
				);
				const selected = selectedHardPhoneSip || selectedSoftPhoneSip;
				const linkId = selected?.sipId ?? sipId;
				sipFlowLog("Popup: claim resolved -> state", {
					claimSipId: sipId,
					linkId,
					stateId: linkId,
					sipValue: selected?.sipValue,
					isSoftPhone: !!selectedSoftPhoneSip,
				});

				if (selectedHardPhoneSip) {
					dispatch(
						sipToDispatcher.actions.setSip({
							id: linkId,
							sipValue: selectedHardPhoneSip.sipValue,
						}),
					);
					dispatch(
						sipToDispatcher.actions.setLineType(
							textTranslate.externalLine,
						),
					);
				}

				if (selectedSoftPhoneSip) {
					dispatch(
						sipToDispatcher.actions.setSip({
							id: linkId,
							sipValue: selectedSoftPhoneSip.sipValue,
						}),
					);
					dispatch(
						sipToDispatcher.actions.setLineType(
							textTranslate.internalLine,
						),
					);
				}

				sessionStorage.setItem(
					"sipToDispatcherId",
					JSON.stringify(sipId),
				);
				sessionStorage.setItem("dispatcherId", JSON.stringify(userId));
				sessionStorage.setItem(
					"sipData",
					JSON.stringify({
						dispatcherId: userId,
						sipToDispatcherId: sipId,
						linkId,
						sipValue:
							selectedHardPhoneSip?.sipValue ||
							selectedSoftPhoneSip?.sipValue,
						lineType: selectedSoftPhoneSip
							? textTranslate.internalLine
							: textTranslate.externalLine,
					}),
				);
			}
			return data?.success === true;
		},
		[
			hardPhoneSipsData,
			softPhoneSipsData,
			userId,
			textTranslate.internalLine,
			textTranslate.externalLine,
			dispatch,
		],
	);

	const softPhoneClaimInFlight = useRef(false);

	const onSubmitSoftPhoneSip = useCallback(async () => {
		if (softPhoneClaimInFlight.current) return null;
		softPhoneClaimInFlight.current = true;
		try {
			// Re-fetch: Redis occupancy is instant, unlike Asterisk `lastCheckMs`,
			// which lags several seconds and caused false "no lines" on switch.
			const { softPhoneSips } = await getSips();
			sipFlowLog("Popup: softphone auto-claim scan", {
				candidates: softPhoneSips.map((sip) => ({
					sipValue: sip.sipValue,
					claimSipId: sip.sipToDispatcherId,
					linkId: sip.sipId,
					occupiedByUserId: sip.occupiedByUserId,
					free: sip.occupiedByUserId == null,
				})),
			});

			for (let i = 0; i < softPhoneSips.length; i++) {
				const sip = softPhoneSips[i];
				// Claim any line whose Redis lock is free; backend re-validates.
				if (sip.occupiedByUserId != null) {
					sipFlowLog("Popup: softphone auto-claim skip", {
						sipValue: sip.sipValue,
						claimSipId: sip.sipToDispatcherId,
						linkId: sip.sipId,
						reason: "occupied",
						occupiedByUserId: sip.occupiedByUserId,
					});
				} else {
					sipFlowLog("Popup: softphone auto-claim try", {
						sipValue: sip.sipValue,
						claimSipId: sip.sipToDispatcherId,
						linkId: sip.sipId,
					});
					const success = await onSubmitSip(sip.sipToDispatcherId);
					if (success) {
						onClose();
						return null;
					}
					sipFlowLog("Popup: softphone auto-claim rejected by backend", {
						sipValue: sip.sipValue,
						claimSipId: sip.sipToDispatcherId,
						linkId: sip.sipId,
					});
				}
			}
			sipFlowLog("Popup: softphone auto-claim -> no free line", {});
			setIsSoftPhoneSipsListEmpty(true);
			return null;
		} finally {
			softPhoneClaimInFlight.current = false;
		}
	}, [getSips, onClose, onSubmitSip]);

	const clearSipSession = useCallback(async () => {
		await sipDisconnect();
		onClose();
	}, [onClose]);

	const [selectedLineType, setSelectedLineType] =
		useState<PopupContent.LineType>(null);

	const isEmptyList =
		(selectedLineType === "external" && isHardPhoneSipsListEmpty) ||
		(selectedLineType === "internal" && isSoftPhoneSipsListEmpty);

	const [showModal, setShowModal] = useState(false);

	const closeModal = useCallback(() => {
		setShowModal(false);
	}, []);

	const openModal = useCallback(() => {
		setShowModal(true);
	}, []);

	const variousSipsChanges = useCallback(
		(lineType: PopupContent.LineType) => {
			if (lineType === "internal") onSubmitSoftPhoneSip();
			if (lineType === "none") clearSipSession();
		},
		[clearSipSession, onSubmitSoftPhoneSip],
	);

	const onConfirm = useCallback(() => {
		variousSipsChanges(selectedLineType);
	}, [selectedLineType, variousSipsChanges]);

	useLayoutEffect(() => {
		if (isLoading) getSips();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	// Selection is applied from button handlers only.
	// Keep this effect for the "active calls" confirm modal path.
	useEffect(() => {
		if (selectedLineType !== "internal" || !callsData.length) return;
		openModal();
	}, [callsData.length, openModal, selectedLineType]);

	useEffect(() => {
		if (selectedLineType !== "none" || !callsData.length) return;
		openModal();
	}, [callsData.length, openModal, selectedLineType]);

	useEffect(() => {
		if (selectedLineType === "external" && !!hardPhoneSipsData.length) {
			const selectOptionsExternal: MultiSelectWithModalBase.Value =
				hardPhoneSipsData.map((sip) => ({
					key: sip.sipToDispatcherId,
					label: sip.sipValue,
					value: sip.sipToDispatcherId,
					name: sip.sipValue,
					data: sip,
					iconBeforeText: (
						<Sip key={sip?.sipToDispatcherId} data={sip} />
					),
				}));

			setSelectOptionsExternal(selectOptionsExternal);
		} else setSelectOptionsExternal([]);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [hardPhoneSipsData, selectedLineType]);

	useEffect(() => {
		if (!choiseSIPidExternal) return;

		(async () => {
			sipFlowLog("Popup: external line picked", {
				claimSipId: choiseSIPidExternal,
			});
			const success = await onSubmitSip(choiseSIPidExternal);
			setChoiseSIPidExternal(null);
			if (success) onClose();
		})();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [choiseSIPidExternal]);

	return (
		<>
			<StyledLabeledField label={textTranslate.lines} />
			{isLoading ? (
				<Loader active dimmed={false} />
			) : (
				<>
					<ControlButton
						onClick={() => {
							sipFlowLog("Popup: line type selected", {
								lineType: "external",
							});
							setSelectedLineType("external");
							setShowModalExternal(true);
						}}
						text={textTranslate.externalSIP}
						variant="white"
					/>

					<ControlButton
						onClick={() => {
							sipFlowLog("Popup: line type selected", {
								lineType: "internal",
							});
							setSelectedLineType("internal");
							if (callsData.length) {
								openModal();
								return;
							}
							onSubmitSoftPhoneSip();
						}}
						text={textTranslate.internalSIP}
						variant="white"
					/>

					<DividerCustomer side="top" />

					<ControlButton
						onClick={() => {
							sipFlowLog("Popup: line type selected", {
								lineType: "none",
							});
							setSelectedLineType("none");
							if (callsData.length) {
								openModal();
								return;
							}
							clearSipSession();
						}}
						text={textTranslate.emptySIP}
						variant="white"
					/>
				</>
			)}
			{isEmptyList && <Message>{textTranslate.noAvailable}</Message>}
			{showModal && (
				<DeleteModal
					label={textTranslate.sipAlert}
					onConfirm={onConfirm}
					onCancel={closeModal}
				/>
			)}
		</>
	);
};

declare namespace PopupContent {
	interface Props {
		onClose: () => void;
		setShowModalExternal: Dispatch<boolean>;
		userId: number;
		setSelectOptionsExternal: (
			value: MultiSelectWithModalBase.Value,
		) => void;
		choiseSIPidExternal: number | null;
		setChoiseSIPidExternal: Dispatch<number | null>;
	}

	type LineType = "internal" | "external" | "none" | null;
}

export default PopupContent;
